import { useState, useEffect, useRef } from "react";
import { useCartData } from "@/zustand/cartStore";
import { useProductData } from "@/zustand/productStore";
import EmailMarketingLeadsProvider from "@/utils/emailMarketing/EmailMarketingLeadsProvider";

// Card number, expiry and CVC inputs are also type="tel", so a bare
// input[type="tel"] lookup can match a payment field when the phone input
// isn't rendered; scope to the contacts group where only the phone field is.
const PHONE_INPUT_SELECTOR = '#group-contacts input[type="tel"]';

const getPhoneNumber = (phoneInput) => {
  if (!phoneInput) return "";
  const instance = window.intlTelInputGlobals?.getInstance(phoneInput);
  return instance?.getNumber() || phoneInput.value || "";
};

export const useEmailMarketingLead = () => {
  const [hasCreatedLead, setHasCreatedLead] = useState(false);
  const [isProviderReady, setIsProviderReady] = useState(false);
  const providerRef = useRef(null);
  const lastSentPhoneRef = useRef("");
  const { selectedProduct } = useCartData();
  const { emailMarketingConfig } = useProductData();
  const isKlaviyoLeadsEnabled = emailMarketingConfig?.isKlaviyoLeadsEnabled;

  useEffect(() => {
    if (!isKlaviyoLeadsEnabled) return;

    const initProvider = async () => {
      try {
        const provider = new EmailMarketingLeadsProvider();
        await provider.initialize();
        providerRef.current = provider;
        setIsProviderReady(true);
      } catch (error) {
        console.error("Failed to initialize email marketing:", error);
      }
    };

    initProvider();
  }, [isKlaviyoLeadsEnabled]);

  useEffect(() => {
    if (hasCreatedLead || !isProviderReady) return;

    const handleBlur = () => {
      if (hasCreatedLead) return;

      const emailInput = document.querySelector('input[name="email"]');
      const firstNameInput = document.querySelector('input[name="first_name"]');
      const phoneInput = document.querySelector(PHONE_INPUT_SELECTOR);

      const email = emailInput?.value || "";
      const firstName = firstNameInput?.value || "";
      const phone = getPhoneNumber(phoneInput);

      if (!email || !firstName) return;

      const emailError = document
        .querySelector('input[name="email"]')
        ?.parentElement?.parentElement?.querySelector(".formError");
      if (emailError && emailError.textContent) return;

      const leadData = {
        email,
        first_name: firstName,
        product_name: selectedProduct?.generic_name,
        product_qty: selectedProduct?.amount,
        ...(phone && { phone }),
      };

      if (!providerRef.current?.validate(leadData)) return;

      setHasCreatedLead(true);
      lastSentPhoneRef.current = phone;

      providerRef.current.createLead(leadData).catch((error) => {
        console.error("Failed to create lead", error);
        setHasCreatedLead(false);
      });
    };

    const emailInput = document.querySelector('input[name="email"]');
    emailInput?.addEventListener("blur", handleBlur);

    return () => {
      emailInput?.removeEventListener("blur", handleBlur);
    };
  }, [hasCreatedLead, isProviderReady]);

  // Phone is filled in after email in the form, so the lead is created without
  // it first; once the customer fills it in, update the same lead (upserted by
  // session nonce on the backend) so the phone number reaches Klaviyo too.
  useEffect(() => {
    if (!hasCreatedLead || !isProviderReady) return;

    const handlePhoneBlur = () => {
      const phoneInput = document.querySelector(PHONE_INPUT_SELECTOR);
      const phone = getPhoneNumber(phoneInput);

      if (!phone || phone === lastSentPhoneRef.current) return;

      const emailInput = document.querySelector('input[name="email"]');
      const firstNameInput = document.querySelector('input[name="first_name"]');

      lastSentPhoneRef.current = phone;

      providerRef.current
        ?.createLead({
          email: emailInput?.value || "",
          first_name: firstNameInput?.value || "",
          product_name: selectedProduct?.generic_name,
          product_qty: selectedProduct?.amount,
          phone,
        })
        .catch((error) => {
          console.error("Failed to update lead with phone", error);
          lastSentPhoneRef.current = "";
        });
    };

    const phoneInput = document.querySelector(PHONE_INPUT_SELECTOR);
    phoneInput?.addEventListener("blur", handlePhoneBlur);

    return () => {
      phoneInput?.removeEventListener("blur", handlePhoneBlur);
    };
  }, [hasCreatedLead, isProviderReady]);

  return hasCreatedLead;
};
